// Indicateurs et comparaisons (M24, lot L6-A) : un indicateur à la fois, fiche du registre unique (unité,
// définition, nature), période, normalisation explicite et son dénominateur, couverture et date de calcul.
// Aucun classement : les lignes suivent le territoire puis le nom. Les grandes masses financières ne
// s'affichent qu'avec le droit financier.
import { INDICATEURS_SUPERVISION, INSTITUTIONS_DROIT_FINANCIER, LIBELLES_NORMALISATION, ficheIndicateur, type LigneComparaison, type Normalisation } from '@cc/contrats';
import { Bouton, EtatVide, PuceEtat, Selection, Tableau, type ColonneTableau } from '@cc/ui';
import { Download } from 'lucide-react';
import { useState } from 'react';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { useSession } from '../../session/FournisseurSession';
import { exporter, useComparaison, useRepertoire, type Filtres } from './api';
import { EtatSources, FiltresTerritoire, NavigationSupervision, provincesDu } from './Communs';
import { libelleEtat, moisCourant, TON_ETAT_VALEUR, valeur } from './logique';
import { SectionFinances } from './SectionFinances';
import styles from '../achats/achats.module.css';
import propres from './supervision.module.css';

export function useDroitFinancier(): boolean {
  const { contexte } = useSession();
  return (INSTITUTIONS_DROIT_FINANCIER as readonly string[]).includes(contexte?.appartenance_active?.institution ?? '');
}

export function PageIndicateurs() {
  const droit = useDroitFinancier();
  const mois = moisCourant();
  const [filtres, setFiltres] = useState<Filtres>({ type: 'commune', mois_debut: mois, mois_fin: mois });
  const [code, setCode] = useState<string>('DOS-10');
  const [normalisation, setNormalisation] = useState<Normalisation>('aucune');
  const tout = useRepertoire({});
  const { provinces, imposee } = provincesDu(tout.data?.perimetre, tout.data?.elements ?? []);
  const disponibles = INDICATEURS_SUPERVISION.filter((i) => droit || !i.financier);
  const ind = disponibles.find((i) => i.code === code) ?? disponibles[0];
  const norm = ind && (ind.normalisations as readonly string[]).includes(normalisation) ? normalisation : 'aucune';
  const requete = { ...filtres, ...(ind?.nature === 'instantane' ? { mois_debut: undefined, mois_fin: undefined } : {}), indicateur: ind?.code ?? 'DOS-10', normalisation: norm };
  const comparaison = useComparaison(requete, Boolean(ind));
  const r = comparaison.data;
  const unite = r?.normalisation.code === 'aucune' ? (r?.indicateur.unite ?? 'nombre') : 'nombre';
  const colonnes: ColonneTableau<LigneComparaison>[] = [
    { id: 'nom', entete: 'Collectivité', enTeteLigne: true, masquable: false, cellule: (l) => `${l.nom}${l.fictive ? ' (fictive)' : ''}` },
    { id: 'province', entete: 'Province', cellule: (l) => l.province_code },
    { id: 'numerateur', entete: 'Valeur brute', numerique: true, cellule: (l) => valeur(l.numerateur, r?.indicateur.unite ?? 'nombre', l.fictive) },
    ...(norm === 'aucune' ? [] : [{ id: 'denominateur', entete: 'Dénominateur', numerique: true, cellule: (l: LigneComparaison) => valeur(l.denominateur, 'nombre') }]),
    { id: 'valeur', entete: norm === 'aucune' ? 'Valeur' : LIBELLES_NORMALISATION[norm], numerique: true, cellule: (l) => valeur(l.valeur, unite, l.fictive) },
    { id: 'etat', entete: 'État', cellule: (l) => <PuceEtat ton={TON_ETAT_VALEUR[l.etat]} libelle={libelleEtat(l.etat)} /> },
    { id: 'motif', entete: 'Motif', cellule: (l) => l.motif ?? '' },
  ];
  const fiche = ind ? ficheIndicateur(ind.code) : undefined;
  const parametresExport = { ...requete, province: filtres.province, type: filtres.type };
  return (
    <>
      <EnTetePage etiquette="Supervision" titre="Indicateurs et comparaisons" description="Un indicateur à la fois, avec son unité, sa période, son dénominateur et la couverture des données. Aucun classement global ni score composite." />
      <NavigationSupervision actif="indicateurs" />
      <div className={styles.grille} role="group" aria-label="Choix de l'indicateur et de la normalisation">
        <Selection libelle="Indicateur" options={disponibles.map((i) => ({ valeur: i.code, libelle: `${i.code} ${ficheIndicateur(i.code)?.libelle ?? ''}` }))} value={ind?.code ?? ''} onChange={(e) => setCode(e.target.value)} />
        <Selection libelle="Normalisation" options={(ind?.normalisations ?? ['aucune']).map((n) => ({ valeur: n, libelle: LIBELLES_NORMALISATION[n] }))} value={norm} onChange={(e) => setNormalisation(e.target.value as Normalisation)} />
      </div>
      <FiltresTerritoire filtres={filtres} onChange={setFiltres} avecPeriode={ind?.nature === 'periode'} provinces={provinces} imposee={imposee} />
      {fiche ? (
        <dl className={propres.fiche} aria-label="Fiche de l'indicateur">
          <dt>Définition</dt>
          <dd>{fiche.definition}</dd>
          <dt>Unité</dt>
          <dd>{fiche.unite === 'montant' ? 'francs CFA' : fiche.unite}</dd>
          <dt>Période</dt>
          <dd>{r?.periode ? `${r.periode.mois_debut} à ${r.periode.mois_fin} (mois civils de Libreville)` : 'instantané au dernier calcul'}</dd>
          <dt>Dénominateur</dt>
          <dd>{r?.normalisation.denominateur ?? 'aucun (valeur brute)'}</dd>
        </dl>
      ) : null}
      <EtatRequete chargement={comparaison.isPending} erreur={comparaison.error} onReessayer={() => void comparaison.refetch()} lot="L6-A" fonction="La comparaison">
        <section className={styles.bloc} aria-labelledby="titre-comparaison">
          <h2 id="titre-comparaison">{r ? `${r.indicateur.code} ${r.indicateur.libelle}` : 'Comparaison'}</h2>
          <EtatSources couverture={r?.couverture} />
          <p className={propres.total} data-testid="total">
            Total : {r?.total === null || r?.total === undefined ? 'non affiché' : valeur(r.total, r.indicateur.unite)}
            {r?.motif_total ? ` (${r.motif_total})` : ''}
          </p>
          <div className={styles.actes}>
            <Bouton icone={Download} onClick={() => void exporter('comparaison', 'csv', parametresExport)}>Exporter en CSV</Bouton>
            <Bouton icone={Download} onClick={() => void exporter('comparaison', 'xlsx', parametresExport)}>Exporter en XLSX</Bouton>
          </div>
          {(r?.elements.length ?? 0) === 0 ? (
            <EtatVide titre="Aucune collectivité">Aucune collectivité ne correspond à ces filtres.</EtatVide>
          ) : (
            <Tableau legende={`Comparaison ${r?.indicateur.code ?? ''}`} colonnes={colonnes} lignes={r?.elements ?? []} cleLigne={(l) => l.collectivite_id} cleColonnes="supervision-comparaison" />
          )}
        </section>
      </EtatRequete>
      <SectionFinances droit={droit} filtres={filtres} />
    </>
  );
}
